import { Eraser } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FC,
  type PointerEvent
} from "react";

import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

import { usePortalT } from "../i18n";

export type SignatureMethod = "drawn" | "typed";
export type SignatureValue = { method: SignatureMethod; image: string };

const OUT_WIDTH = 600;
const OUT_HEIGHT = 200;
const INK = "#0f172a";
const SCRIPT_FONT =
  "'Segoe Script', 'Brush Script MT', 'Snell Roundhand', 'Apple Chancery', cursive";

/** Scales the pad into a fixed-size PNG, so the upload stays small. */
const exportCanvas = (source: HTMLCanvasElement): string => {
  const out = document.createElement("canvas");
  out.width = OUT_WIDTH;
  out.height = OUT_HEIGHT;
  const ctx = out.getContext("2d");
  if (!ctx) return "";
  const scale = Math.min(OUT_WIDTH / source.width, OUT_HEIGHT / source.height);
  const w = source.width * scale;
  const h = source.height * scale;
  ctx.drawImage(source, (OUT_WIDTH - w) / 2, (OUT_HEIGHT - h) / 2, w, h);
  return out.toDataURL("image/png");
};

export const typedSignature = (name: string): string => {
  const out = document.createElement("canvas");
  out.width = OUT_WIDTH;
  out.height = OUT_HEIGHT;
  const ctx = out.getContext("2d");
  if (!ctx) return "";
  let size = 72;
  ctx.font = `${size}px ${SCRIPT_FONT}`;
  while (size > 24 && ctx.measureText(name).width > OUT_WIDTH - 40) {
    size -= 4;
    ctx.font = `${size}px ${SCRIPT_FONT}`;
  }
  ctx.fillStyle = INK;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(name, OUT_WIDTH / 2, OUT_HEIGHT / 2);
  return out.toDataURL("image/png");
};

const DrawPad: FC<{
  disabled: boolean;
  onDrawn: (image: string | null) => void;
}> = ({ disabled, onDrawn }) => {
  const { t } = usePortalT();
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);

  const context = () => canvas.current?.getContext("2d") ?? null;

  // Match the backing store to the element so strokes aren't blurry.
  useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    const ratio = window.devicePixelRatio || 1;
    el.width = el.offsetWidth * ratio;
    el.height = el.offsetHeight * ratio;
    const ctx = el.getContext("2d");
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = INK;
  }, []);

  const point = (e: PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e: PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    const ctx = context();
    if (!ctx) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    const { x, y } = point(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + 0.1, y + 0.1);
    ctx.stroke();
  };

  const move = (e: PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = context();
    if (!ctx) return;
    const { x, y } = point(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const end = () => {
    if (!drawing.current || !canvas.current) return;
    drawing.current = false;
    setEmpty(false);
    onDrawn(exportCanvas(canvas.current));
  };

  const clear = () => {
    const el = canvas.current;
    const ctx = context();
    if (!el || !ctx) return;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, el.width, el.height);
    ctx.restore();
    setEmpty(true);
    onDrawn(null);
  };

  return (
    <div className="relative">
      <canvas
        ref={canvas}
        className={cn(
          "h-40 w-full touch-none rounded-lg border border-dashed border-border bg-background",
          disabled ? "cursor-not-allowed" : "cursor-crosshair"
        )}
        aria-label={t("sign.drawHint")}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        data-testid="signature-canvas"
      />
      {empty ? (
        <span className="pointer-events-none absolute inset-x-0 bottom-3 text-center text-xs text-muted-foreground">
          {t("sign.drawHint")}
        </span>
      ) : null}
      <span className="pointer-events-none absolute inset-x-6 bottom-9 border-b border-border" />
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="absolute end-1.5 top-1.5"
        onClick={clear}
        disabled={disabled || empty}
      >
        <Eraser />
        {t("sign.clear")}
      </Button>
    </div>
  );
};

type Props = {
  /** The signer's full name; typed signatures render it. */
  name: string;
  disabled?: boolean;
  onChange: (value: SignatureValue | null) => void;
};

/** Draw with a finger or mouse, or type a name rendered in a script face. */
export const SignaturePad: FC<Props> = ({
  name,
  disabled = false,
  onChange
}) => {
  const { t } = usePortalT();
  const [method, setMethod] = useState<SignatureMethod>("drawn");
  const [drawn, setDrawn] = useState<string | null>(null);

  const emit = useCallback(
    (next: SignatureMethod, image: string | null) =>
      onChange(image ? { method: next, image } : null),
    [onChange]
  );

  const typedName = name.trim();
  useEffect(() => {
    if (method !== "typed") return;
    emit("typed", typedName ? typedSignature(typedName) : null);
  }, [emit, method, typedName]);

  return (
    <div className="flex flex-col gap-2.5">
      <Tabs
        value={method}
        onValueChange={(v) => {
          const next = v as SignatureMethod;
          setMethod(next);
          if (next === "drawn") emit("drawn", drawn);
        }}
      >
        <TabsList className="w-full">
          <TabsTrigger value="drawn" className="flex-1" disabled={disabled}>
            {t("sign.draw")}
          </TabsTrigger>
          <TabsTrigger value="typed" className="flex-1" disabled={disabled}>
            {t("sign.type")}
          </TabsTrigger>
        </TabsList>
      </Tabs>
      {/* Hidden, not unmounted, so switching tabs keeps the strokes. */}
      <div className={method === "drawn" ? undefined : "hidden"}>
        <DrawPad
          disabled={disabled}
          onDrawn={(image) => {
            setDrawn(image);
            emit("drawn", image);
          }}
        />
      </div>
      {method === "drawn" ? null : (
        <div
          className="flex h-40 items-center justify-center overflow-hidden rounded-lg border border-dashed border-border bg-background px-4 text-4xl text-fg"
          style={{ fontFamily: SCRIPT_FONT }}
          data-testid="signature-typed"
        >
          {typedName || (
            <span className="font-sans text-sm text-muted-foreground">
              {t("sign.fullName")}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
